import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { deliveryService, refundService } from '../services';
import useBottomNavPadding from '../hooks/useBottomNavPadding';
import PageHeader from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { ImageUploader } from '../components/common/ImageUploader';

interface RefundForm {
  deliveryId: string;
  reason: string;
  customReason: string;
  images: string[];
}

const REFUND_REASONS = [
  { value: 'damaged_products', key: 'refund.reasons.damaged_products' },
  { value: 'poor_quality', key: 'refund.reasons.poor_quality' },
  { value: 'missing_items', key: 'refund.reasons.missing_items' },
  { value: 'wrong_products', key: 'refund.reasons.wrong_products' },
  { value: 'late_delivery', key: 'refund.reasons.late_delivery' },
  { value: 'delivery_not_received', key: 'refund.reasons.delivery_not_received' },
  { value: 'other', key: 'refund.reasons.other' }
];

export default function RefundRequest() {
  const [delivery, setDelivery] = useState<any>();
  const [form, setForm] = useState<RefundForm>({
    deliveryId: '',
    reason: '',
    customReason: '',
    images: []
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [fetchingDeliveries, setFetchingDeliveries] = useState<boolean>(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { deliveryId } = useParams();
  const [searchParams] = useSearchParams();

  useBottomNavPadding();

  useEffect(() => {
    const preselectedDeliveryId = deliveryId || searchParams.get('deliveryId');
    if (preselectedDeliveryId) {
      fetchSpecificDelivery(preselectedDeliveryId);
    }
  }, [deliveryId, searchParams]);

  const fetchSpecificDelivery = async (specificDeliveryId: string) => {
    try {
      setFetchingDeliveries(true);
      // First get all user delivery to find the specific one
      const specificDelivery = await deliveryService.getDeliveryById(specificDeliveryId);

      console.log('specificDelivery', specificDelivery)
      
      if (specificDelivery) {
        setDelivery(specificDelivery);
        setForm(prev => ({ ...prev, deliveryId: specificDelivery.id }));
      } else {
        setError(t('refund.error.deliveryNotFound'));
        // Fallback to showing all delivery
      }
    } catch (err) {
      console.error('Error fetching specific delivery:', err);
      setError(t('refund.error.fetchDeliveries'));
      // Fallback to showing all delivery
    } finally {
      setFetchingDeliveries(false);
    }
  };

  const handleInputChange = (field: keyof RefundForm, value: string | string[]) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleImagesChange = (newImages: string[]) => {
    setForm(prev => ({ ...prev, images: newImages }));
  };

  const validateForm = (): boolean => {
    if (!form.deliveryId) {
      setError(t('refund.error.selectDelivery'));
      return false;
    }

    if (!form.reason) {
      setError(t('refund.error.selectReason'));
      return false;
    }

    if (form.reason === 'other' && !form.customReason.trim()) {
      setError(t('refund.error.enterCustomReason'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Find the selected delivery from the array
      if (!delivery) {
        throw new Error('Selected delivery not found');
      }

      // Get final reason text
      const finalReason = form.reason === 'other' ? form.customReason : form.reason;

      // Create refund application for the delivery with images
      await refundService.createRefundApplication(
        form.deliveryId,
        finalReason,
        0,
        form.images
      );

      setSuccess(true);
      toast.success(t('refund.success.submitted'));

      // Redirect to refund history after a short delay
      setTimeout(() => {
        navigate('/refund-history');
      }, 2000);

    } catch (err: any) {
      console.error('Error submitting refund request:', err);
      const errorMessage = err.response?.data?.error || err.message || t('refund.error.submitFailed');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (success) {
    return (
      <main className="min-h-screen bg-gray-50">
        <PageHeader title={t('refund.pageTitle')} backPath="/me" />
        <div className="p-4">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">{t('refund.success.title')}</h2>
            <p className="text-gray-600 mb-4">{t('refund.success.message')}</p>
            <Button onClick={() => navigate('/refund-history')}>
              {t('refund.success.viewHistory')}
            </Button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50">
      <PageHeader title={t('refund.pageTitle')} backPath="/me" />

      <div className="p-4">
        {fetchingDeliveries ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : !delivery ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-lg font-medium mb-2">{t('refund.noDeliveries.title')}</h2>
            <p className="text-gray-600 mb-4">{t('refund.noDeliveries.message')}</p>
            <Button onClick={() => navigate('/delivery-activity')}>
              {t('refund.noDeliveries.viewDeliveries')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Delivery Info Display */}
            {delivery && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">{t('refund.form.deliveryDetails')}</h3>
                <div className="text-sm text-blue-800 space-y-1">
                  <p>{t('refund.form.deliveryDate')}: {formatDate(delivery.date)}</p>
                  <p>
                    {t('refund.form.subscriptionType')}:{' '}
                    {delivery.subscription.plan.charAt(0).toUpperCase() +
                      delivery.subscription.plan.slice(1)}
                  </p>
                  <p>
                    {t('subscription.serviceType.title')}:{' '}
                    {delivery.subscription.serviceType.charAt(0).toUpperCase() +
                      delivery.subscription.serviceType.slice(1)}
                  </p>
                </div>
              </div>
            )}

            {/* Reason Selection */}
            <div className="bg-white rounded-lg shadow p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('refund.form.selectReason')} *
              </label>
              <div className="space-y-2">
                {REFUND_REASONS.map((reason) => (
                  <label key={reason.value} className="flex items-center">
                    <input
                      type="radio"
                      name="reason"
                      value={reason.value}
                      checked={form.reason === reason.value}
                      onChange={(e) => handleInputChange('reason', e.target.value)}
                      className="mr-3"
                    />
                    <span className="text-sm">{t(reason.key)}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Reason Input */}
            {form.reason === 'other' && (
              <div className="bg-white rounded-lg shadow p-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('refund.form.customReason')} *
                </label>
                <textarea
                  value={form.customReason}
                  onChange={(e) => handleInputChange('customReason', e.target.value)}
                  placeholder={t('refund.form.customReasonPlaceholder')}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={4}
                  maxLength={500}
                  required
                />
                <div className="text-xs text-gray-500 mt-1">
                  {form.customReason.length}/500 characters
                </div>
              </div>
            )}

            {/* Image Upload Section */}
            <div className="bg-white rounded-lg shadow p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('refund.form.attachImages')}
              </label>
              <p className="text-sm text-gray-500 mb-3">
                {t('refund.form.attachImagesDescription')}
              </p>
              <ImageUploader
                images={form.images}
                onChange={handleImagesChange}
                maxImages={5}
                isEditMode={true}
              />
            </div>

            {/* Important Notice */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium mb-1">{t('refund.notice.title')}</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>{t('refund.notice.processing')}</li>
                    <li>{t('refund.notice.evidence')}</li>
                    <li>{t('refund.notice.contact')}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="bg-white rounded-lg shadow p-4">
              <button
                type='submit'
                disabled={
                  loading ||
                  !form.deliveryId ||
                  !form.reason ||
                  (form.reason === 'other' && !form.customReason.trim())
                }
                className={`w-full ${
                  loading ||
                  !form.deliveryId ||
                  !form.reason ||
                  (form.reason === 'other' && !form.customReason.trim())
                    ? 'bg-gray-200 cursor-not-allowed text-gray-500'
                    : 'bg-yellow-500 hover:bg-yellow-600'
                }`}
              >
                {loading
                  ? t('refund.form.submitting')
                  : t('refund.form.submitRequest')}
              </button>
            </div>
          </form>
        )}
      </div>
    </main>
  );
} 